import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PushPinIcon from "@mui/icons-material/PushPin";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import { ChatContext } from "../context/chatcontext";
import SearchBar from "./SearchBar";
import { EmptyChatListState } from "./EmptyState";
import { exportChat } from "../utils/exportUtils";
import { toast } from "react-toastify";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/apiKey";

export default function Sidebar() {
  const {
    chats,
    currentChat,
    messages,
    startNewChat,
    switchChat,
    deleteChat,
    updateChatTitle,
    createProject,
    projects,
    getChatsByProject,
    currentProject,
    setCurrentProject,
    togglePinChat,
    toggleArchiveChat,
  } = useContext(ChatContext);

  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [selectedProject, setSelectedProject] = useState(currentProject);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  useEffect(() => setSelectedProject(currentProject), [currentProject]);

  const handleNewChat = () => startNewChat(selectedProject);
  const handleCreateProject = () => {
    const name = prompt("Enter new project name:");
    if (name) {
      const proj = createProject({ name });
      setSelectedProject(proj.id);
      toast.success(`Project "${name}" created!`);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query.toLowerCase());
  };

  const handleExportChat = () => {
    if (!currentChat || messages.length === 0) {
      toast.error("No chat to export");
      return;
    }
    const chat = chats.find((c) => c.id === currentChat);
    const title = chat?.title || "Chat Export";
    exportChat(messages, title, "markdown");
    toast.success("Chat exported successfully!");
  };

  // Separate normal chats from project chats
  const { normalChats, projectChatsGrouped } = useMemo(() => {
    const normal = chats.filter((c) => !c.projectId && !c.is_archived);
    const projectChats = chats.filter((c) => c.projectId && !c.is_archived);
    
    // Group project chats by project
    const grouped = {};
    projectChats.forEach((chat) => {
      const projId = chat.projectId;
      if (!grouped[projId]) {
        grouped[projId] = [];
      }
      grouped[projId].push(chat);
    });
    
    // Sort each group: pinned first, then by date
    Object.keys(grouped).forEach((projId) => {
      grouped[projId].sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
    });
    
    // Sort normal chats: pinned first, then by date
    normal.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    
    return { normalChats: normal, projectChatsGrouped: grouped };
  }, [chats]);

  // Filter chats based on search query
  const filterChats = (chatList) => {
    if (!searchQuery) return chatList;
    return chatList.filter((chat) =>
      chat.title?.toLowerCase().includes(searchQuery) ||
      chat.messages?.some((msg) =>
        msg.content?.toLowerCase().includes(searchQuery)
      )
    );
  };

  // Get chats to display based on selected project
  const displayChats = useMemo(() => {
    if (selectedProject) {
      // Show only chats from selected project
      const projectChats = projectChatsGrouped[selectedProject] || [];
      return filterChats(projectChats);
    } else {
      // Show normal chats only (project chats shown separately below)
      return filterChats(normalChats);
    }
  }, [selectedProject, normalChats, projectChatsGrouped, searchQuery]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        background: "#f8fafc",
        color: "#0f172a",
        display: "flex",
        flexDirection: "column",
        p: 2,
        borderRight: { xs: "none", md: "1px solid #e2e8f0" },
        boxShadow: "none",
        overflow: "hidden",
      }}
    >
      {/* 🟣 New Chat */}
      <Button
        fullWidth
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleNewChat}
        sx={{
          mb: 2,
          background: "#1e3a8a",
          "&:hover": { background: "#1e40af" },
          textTransform: "none",
          fontWeight: 600,
          color: "#ffffff",
          py: 1.2,
        }}
      >
        New Chat
      </Button>

      {/* 📁 Create Project */}
      <Button
        variant="outlined"
        startIcon={<FolderOpenIcon />}
        onClick={handleCreateProject}
        sx={{
          mb: 2,
          color: "#1e3a8a",
          borderColor: "#1e3a8a",
          "&:hover": {
            borderColor: "#1e40af",
            backgroundColor: "rgba(30, 58, 138, 0.05)",
          },
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        Create Project
      </Button>

      {/* 🔽 Project Filter */}
      <select
        value={selectedProject || ""}
        onChange={(e) => {
          const val = e.target.value ? Number(e.target.value) : null;
          setSelectedProject(val);
          setCurrentProject(val);
        }}
        style={{
          background: "#ffffff",
          color: "#1e3a8a",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          padding: "10px",
          marginBottom: "15px",
          outline: "none",
          fontSize: "14px",
          fontWeight: "500",
          cursor: "pointer",
        }}
      >
        <option value="">All Projects</option>
        {projects.map((proj) => (
          <option key={proj.id} value={proj.id}>
            {proj.name}
          </option>
        ))}
      </select>

      <Divider sx={{ borderColor: "#e2e8f0", mb: 2 }} />
      
      {/* Search Bar */}
      <SearchBar onSearch={handleSearch} placeholder="Search chats..." />

      {/* 💬 Chats List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: "10px",
          },
        }}
      >
        <List sx={{ py: 0 }}>
          {/* Normal Chats Section (only when "All Projects" is selected) */}
          {!selectedProject && (
            <>
              {normalChats.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      mt: 1,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      px: 2,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    Normal Chats
                  </Typography>
                  {filterChats(normalChats).map((chat) => (
                    <ListItem
                      key={chat.id}
                      disablePadding
                      sx={{
                        mb: 0.5,
                        borderRadius: "8px",
                        bgcolor:
                          chat.id === currentChat
                            ? "rgba(30, 58, 138, 0.08)"
                            : "transparent",
                        "&:hover": {
                          bgcolor: chat.id === currentChat 
                            ? "rgba(30, 58, 138, 0.12)" 
                            : "rgba(30, 58, 138, 0.04)",
                        }
                      }}
                    >
                      <ListItemButton 
                        onClick={() => switchChat(chat.id)}
                        sx={{ py: 1, px: 2, borderRadius: "8px" }}
                      >
                        {chat.is_pinned && (
                          <PushPinIcon sx={{ fontSize: "0.9rem", mr: 1, color: "#1e3a8a" }} />
                        )}
                        <ListItemText
                          primary={
                            <Typography
                              sx={{
                                color:
                                  chat.id === currentChat ? "#1e3a8a" : "#334155",
                                fontSize: "0.9rem",
                                fontWeight:
                                  chat.id === currentChat ? "600" : "500",
                              }}
                              noWrap
                            >
                              {chat.title}
                            </Typography>
                          }
                          secondary={
                            chat.messages?.length > 0 ? (
                              <Typography
                                sx={{
                                  color: "#64748b",
                                  fontSize: "0.75rem",
                                  mt: 0.25,
                                }}
                                noWrap
                              >
                                {(() => {
                                  const lastMessage = chat.messages[chat.messages.length - 1];
                                  if (!lastMessage) return "";
                                  const content = lastMessage.content;
                                  if (typeof content === "string") {
                                    return content.substring(0, 50);
                                  }
                                  return String(content || "").substring(0, 50);
                                })()}
                              </Typography>
                            ) : null
                          }
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuAnchor(e.currentTarget);
                            setSelectedChatId(chat.id);
                          }}
                          sx={{
                            color: "#94a3b8",
                            ml: 0.5,
                            "&:hover": {
                              color: "#1e3a8a",
                              bgcolor: "rgba(30, 58, 138, 0.05)",
                            },
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </>
              )}
              
              {/* Project Chats Section (grouped by project) */}
              {Object.keys(projectChatsGrouped).length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      mt: normalChats.length > 0 ? 3 : 1,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      px: 2,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    Projects
                  </Typography>
                  {Object.entries(projectChatsGrouped).map(([projId, projChats]) => {
                    const project = projects.find((p) => p.id === Number(projId));
                    const filteredProjChats = filterChats(projChats);
                    
                    if (filteredProjChats.length === 0) return null;
                    
                    return (
                      <Box key={projId} sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            py: 0.5,
                            color: "#1e3a8a",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            display: "block",
                          }}
                        >
                          📁 {project?.name || `Project ${projId}`}
                        </Typography>
                        {filteredProjChats.map((chat) => (
                          <ListItem
                            key={chat.id}
                            disablePadding
                            sx={{
                              mb: 0.5,
                              ml: 1,
                              borderRadius: "8px",
                              bgcolor:
                                chat.id === currentChat
                                  ? "rgba(30, 58, 138, 0.08)"
                                  : "transparent",
                              "&:hover": {
                                bgcolor: chat.id === currentChat 
                                  ? "rgba(30, 58, 138, 0.12)" 
                                  : "rgba(30, 58, 138, 0.04)",
                              }
                            }}
                          >
                            <ListItemButton 
                              onClick={() => switchChat(chat.id)}
                              sx={{ py: 0.8, px: 1.5, borderRadius: "8px" }}
                            >
                              {chat.is_pinned && (
                                <PushPinIcon sx={{ fontSize: "0.85rem", mr: 1, color: "#1e3a8a" }} />
                              )}
                              <ListItemText
                                primary={
                                  <Typography
                                    sx={{
                                      color:
                                        chat.id === currentChat ? "#1e3a8a" : "#334155",
                                      fontSize: "0.85rem",
                                      fontWeight:
                                        chat.id === currentChat ? "600" : "500",
                                    }}
                                    noWrap
                                  >
                                    {chat.title}
                                  </Typography>
                                }
                                secondary={
                                  chat.messages?.length > 0 ? (
                                    <Typography
                                      sx={{
                                        color: "#64748b",
                                        fontSize: "0.7rem",
                                        mt: 0.25,
                                      }}
                                      noWrap
                                    >
                                      {(() => {
                                  const lastMessage = chat.messages[chat.messages.length - 1];
                                  if (!lastMessage) return "";
                                  const content = lastMessage.content;
                                  if (typeof content === "string") {
                                    return content.substring(0, 50);
                                  }
                                  return String(content || "").substring(0, 50);
                                })()}
                                    </Typography>
                                  ) : null
                                }
                              />
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuAnchor(e.currentTarget);
                                  setSelectedChatId(chat.id);
                                }}
                                sx={{
                                  color: "#94a3b8",
                                  ml: 0.5,
                                  "&:hover": {
                                    color: "#1e3a8a",
                                    bgcolor: "rgba(30, 58, 138, 0.05)",
                                  },
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </Box>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* Selected Project Chats (when a project is selected) */}
          {selectedProject && (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  mt: 1,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  px: 2,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              >
                {projects.find((p) => p.id === selectedProject)?.name || "Project"} Chats
              </Typography>
              {displayChats.length === 0 ? (
                <EmptyChatListState onNewChat={handleNewChat} />
              ) : (
                displayChats.map((chat) => (
                  <ListItem
                    key={chat.id}
                    disablePadding
                    sx={{
                      mb: 0.5,
                      borderRadius: "8px",
                      bgcolor:
                        chat.id === currentChat
                          ? "rgba(30, 58, 138, 0.08)"
                          : "transparent",
                      "&:hover": {
                        bgcolor: chat.id === currentChat 
                          ? "rgba(30, 58, 138, 0.12)" 
                          : "rgba(30, 58, 138, 0.04)",
                      }
                    }}
                  >
                    <ListItemButton 
                      onClick={() => switchChat(chat.id)}
                      sx={{ py: 1, px: 2, borderRadius: "8px" }}
                    >
                      {chat.is_pinned && (
                        <PushPinIcon sx={{ fontSize: "0.9rem", mr: 1, color: "#1e3a8a" }} />
                      )}
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              color:
                                chat.id === currentChat ? "#1e3a8a" : "#334155",
                              fontSize: "0.9rem",
                              fontWeight:
                                chat.id === currentChat ? "600" : "500",
                            }}
                            noWrap
                          >
                            {chat.title}
                          </Typography>
                        }
                        secondary={
                          chat.messages?.length > 0 ? (
                            <Typography
                              sx={{
                                color: "#64748b",
                                fontSize: "0.75rem",
                                mt: 0.25,
                              }}
                              noWrap
                            >
                              {(() => {
                                const lastMessage = chat.messages[chat.messages.length - 1];
                                if (!lastMessage) return "";
                                const content = lastMessage.content;
                                if (typeof content === "string") {
                                  return content.substring(0, 50);
                                }
                                return String(content || "").substring(0, 50);
                              })()}
                            </Typography>
                          ) : null
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuAnchor(e.currentTarget);
                          setSelectedChatId(chat.id);
                        }}
                        sx={{
                          color: "#94a3b8",
                          ml: 0.5,
                          "&:hover": {
                            color: "#1e3a8a",
                            bgcolor: "rgba(30, 58, 138, 0.05)",
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </>
          )}

          {/* Empty state when no chats at all */}
          {!selectedProject && normalChats.length === 0 && Object.keys(projectChatsGrouped).length === 0 && (
            <EmptyChatListState onNewChat={handleNewChat} />
          )}
        </List>
      </Box>

      {/* 3-dots Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setSelectedChatId(null);
        }}
        PaperProps={{
          sx: {
            bgcolor: "#ffffff",
            color: "#0f172a",
            border: "1px solid #e2e8f0",
            minWidth: 180,
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {selectedChatId && (() => {
          const chat = chats.find((c) => c.id === selectedChatId);
          if (!chat) return null;
          
          return [
            <MenuItem
              key="edit"
              onClick={() => {
                const newTitle = prompt("Edit chat title:", chat.title || "New Chat");
                if (newTitle && newTitle.trim() && newTitle !== chat.title) {
                  updateChatTitle(selectedChatId, newTitle);
                  toast.success("Chat title updated!");
                }
                setMenuAnchor(null);
                setSelectedChatId(null);
              }}
              sx={{ fontSize: "0.875rem", py: 1.2 }}
            >
              <EditIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: "#1e3a8a" }} />
              Edit chat
            </MenuItem>,
            <MenuItem
              key="share"
              onClick={async () => {
                try {
                  const chatToShare = chats.find((c) => c.id === selectedChatId);
                  if (!chatToShare) {
                    toast.error("Chat not found");
                    setMenuAnchor(null);
                    setSelectedChatId(null);
                    return;
                  }

                  // Get messages for this chat
                  let chatMessages = chatToShare.messages || [];
                  if (selectedChatId === currentChat) {
                    chatMessages = messages;
                  } else {
                    // Fetch messages if not loaded
                    try {
                      const token = localStorage.getItem("auth_token");
                      if (token) {
                        const response = await fetch(`${API_BASE_URL}/api/chat/chats/${selectedChatId}`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (response.ok) {
                          // Check if response is HTML
                          const responseText = await response.text();
                          const contentType = response.headers.get("content-type") || "";
                          const isHtml = 
                            contentType.includes("text/html") ||
                            responseText.trim().toLowerCase().startsWith("<!doctype") || 
                            responseText.trim().toLowerCase().startsWith("<html");
                          
                          if (!isHtml) {
                            try {
                              const data = JSON.parse(responseText);
                              chatMessages = data.messages || [];
                            } catch (e) {
                              console.error("Failed to parse messages response as JSON:", e);
                            }
                          }
                        }
                      }
                    } catch (err) {
                      console.error("Error fetching messages for share:", err);
                    }
                  }

                  // Format chat for sharing
                  const chatText = `Chat: ${chatToShare.title}\n\n${chatMessages.map((m) => `${m.role === "user" ? "You" : "AI"}: ${m.content}`).join("\n\n")}`;
                  
                  // Try native share API
                  if (navigator.share) {
                    await navigator.share({
                      title: `Share: ${chatToShare.title}`,
                      text: chatText,
                    });
                    toast.success("Chat shared!");
                  } else {
                    // Fallback: copy to clipboard
                    await navigator.clipboard.writeText(chatText);
                    toast.success("Chat copied to clipboard!");
                  }
                } catch (err) {
                  console.error("Share failed:", err);
                  toast.error("Failed to share chat");
                }
                setMenuAnchor(null);
                setSelectedChatId(null);
              }}
              sx={{ fontSize: "0.875rem", py: 1.2 }}
            >
              <ShareIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: "#1e3a8a" }} />
              Share chat
            </MenuItem>,
            <MenuItem
              key="pin"
              onClick={() => {
                togglePinChat(selectedChatId);
                setMenuAnchor(null);
                setSelectedChatId(null);
              }}
              sx={{ fontSize: "0.875rem", py: 1.2 }}
            >
              <PushPinIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: "#1e3a8a" }} />
              {chat.is_pinned ? "Unpin chat" : "Pin chat"}
            </MenuItem>,
            <MenuItem
              key="archive"
              onClick={() => {
                toggleArchiveChat(selectedChatId);
                setMenuAnchor(null);
                setSelectedChatId(null);
              }}
              sx={{ fontSize: "0.875rem", py: 1.2 }}
            >
              {chat.is_archived ? (
                <UnarchiveIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: "#1e3a8a" }} />
              ) : (
                <ArchiveIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: "#1e3a8a" }} />
              )}
              {chat.is_archived ? "Unarchive chat" : "Archive chat"}
            </MenuItem>,
            <MenuItem
              key="export"
              onClick={() => {
                if (selectedChatId === currentChat && messages.length > 0) {
                  const chat = chats.find((c) => c.id === selectedChatId);
                  exportChat(messages, chat?.title || "Chat Export", "markdown");
                  toast.success("Chat exported!");
                } else {
                  toast.error("Please select the chat to export");
                }
                setMenuAnchor(null);
                setSelectedChatId(null);
              }}
              sx={{ fontSize: "0.875rem", py: 1.2 }}
            >
              <FileDownloadIcon sx={{ mr: 1.5, fontSize: "1.1rem", color: "#1e3a8a" }} />
              Export chat
            </MenuItem>,
            <MenuItem
              key="delete"
              onClick={() => {
                setMenuAnchor(null);
                setSelectedChatId(null);
                if (window.confirm("Delete this chat?")) {
                  deleteChat(selectedChatId);
                  toast.success("Chat deleted");
                }
              }}
              sx={{ fontSize: "0.875rem", py: 1.2, color: "#ef4444" }}
            >
              <DeleteOutlineIcon sx={{ mr: 1.5, fontSize: "1.1rem" }} />
              Delete chat
            </MenuItem>,
          ];
        })()}
      </Menu>

      {/* Export button for current chat */}
      {currentChat && messages.length > 0 && (
            <Button
          fullWidth
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportChat}
          sx={{
            mt: 1,
            color: "#1e3a8a",
            borderColor: "#1e3a8a",
            "&:hover": {
              borderColor: "#1e40af",
              backgroundColor: "rgba(30, 58, 138, 0.05)",
            },
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          Export Chat
        </Button>
      )}

      <Divider sx={{ borderColor: "#e2e8f0", mt: 2, mb: 2 }} />

      {/* Settings & Logout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Button
          fullWidth
          variant="text"
          startIcon={<SettingsIcon />}
          onClick={() => navigate("/settings")}
          sx={{
            justifyContent: "flex-start",
            color: "#475569",
            textTransform: "none",
            fontSize: "0.9rem",
            fontWeight: 500,
            px: 2,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "rgba(30, 58, 138, 0.05)",
              color: "#1e3a8a",
            },
          }}
        >
          Settings
        </Button>
        <Button
          fullWidth
          variant="text"
          startIcon={<LogoutIcon />}
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          sx={{
            justifyContent: "flex-start",
            color: "#ef4444",
            textTransform: "none",
            fontSize: "0.9rem",
            fontWeight: 500,
            px: 2,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "rgba(239, 68, 68, 0.05)",
              color: "#dc2626",
            },
          }}
        >
          Logout
        </Button>
      </Box>

      <Typography
        variant="caption"
        sx={{
          textAlign: "center",
          color: "#94a3b8",
          mt: 1,
          fontSize: "0.7rem",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        v1.0 · exivis
      </Typography>
    </Box>
  );
}
